/*
 * Herzwarm Motion – markeneigene Bewegung, ohne Bibliotheken.
 * 1. Ladebalken laden sich auf, sobald sie ins Bild scrollen („Warm aufgeladen“).
 *    Die Füllung (.hw-lade__fill) ist immer 100 % breit und über --hw-fill-ratio auf den
 *    Zielwert skaliert; animiert wird nur transform: scaleX (Compositor, kein Layout).
 * 2. Sichtbarkeit: Elemente mit [data-hw-reveal] bekommen die Klasse is-sichtbar (Scroll-Reveal
 *    selbst ist seit 23.10. abgeschaltet; die Klasse steuert nur noch Ladebalken, Trenner,
 *    Tagesladung und Schritte). --hw-i für [data-hw-reveal-kind] setzt Liquid inline.
 *    Startzustände werden erst im Observer-Callback gesetzt (kein erzwungenes Layout beim Laden).
 * 3. Warenkorb: Set-Ladebalken ([data-hw-versand], snippets/hw-versand-fortschritt.liquid) füllt
 *    sich beim Öffnen des Drawers von 0 und nach jedem Section-Re-Render vom alten auf den
 *    neuen Wert (data-hw-prozent, als scaleX); der Stempel bei 100 % skaliert ein. Horizon
 *    zeichnet den Drawer per Hydration-Morph neu, darum MutationObserver statt Listener auf dem Balken.
 * 4. Werte-Band: Pause-Button toggelt .hw-band--pause (Laufschrift anhalten/fortsetzen).
 * Mit „Bewegung reduzieren“ läuft nur 4; 1–3 entfallen.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;
  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const html = document.documentElement;

  const bereit = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };

  /* 4. Werte-Band: Pause-Button (sections/hw-band.liquid), toggelt .hw-band--pause.
        Bei reduzierter Bewegung steht das Band ohnehin, der Button ist per CSS ausgeblendet. */
  const bandGesehen = new WeakSet();
  const band = (wurzel = document) => {
    wurzel.querySelectorAll('[data-hw-band-pause]').forEach((btn) => {
      if (bandGesehen.has(btn)) return;
      bandGesehen.add(btn);
      const band = btn.closest('.hw-band');
      if (!band) return;
      btn.addEventListener('click', () => {
        const pause = band.classList.toggle('hw-band--pause');
        btn.setAttribute('aria-pressed', pause ? 'true' : 'false');
        btn.setAttribute('aria-label', pause ? btn.dataset.labelWeiter : btn.dataset.labelPause);
      });
    });
  };
  bereit(() => band());
  document.addEventListener('shopify:section:load', (e) => band(e.target));

  if (ruhig || !Element.prototype.animate) return;
  html.classList.add('js-hw-motion');

  /* 1. Ladebalken */
  const AUSNAHME = '.hw-versand, cart-drawer-component, .cart-drawer, [data-hw-motion="aus"]';
  const gesehen = new WeakSet();
  const weich = 'cubic-bezier(0.16, 1, 0.3, 1)';

  const laden = (balken) => {
    const fill = balken.querySelector('.hw-lade__fill');
    if (!fill) return;
    fill.style.transform = '';
    // Zielwert aus der Custom Property (wird nicht animiert, daher auch während der
    // CSS-Animation hw-laden stabil); ohne Variable volle Breite.
    const ratio = parseFloat(getComputedStyle(fill).getPropertyValue('--hw-fill-ratio'));
    const ziel = Number.isFinite(ratio) ? `scaleX(${Math.min(1, Math.max(0, ratio))})` : 'scaleX(1)';
    fill.animate([{ transform: 'scaleX(0)' }, { transform: ziel }], {
      duration: 700,
      easing: weich,
    });
  };

  // Erster Callback je Balken: was schon im Bild ist, übernimmt die CSS-Animation hw-laden
  // (keine Doppelung); der Rest startet bei scaleX(0) und lädt beim Sichtbarwerden.
  const balkenNeu = new WeakSet();
  const balkenBeobachter = new IntersectionObserver(
    (eintraege) => {
      for (const e of eintraege) {
        if (balkenNeu.has(e.target)) {
          balkenNeu.delete(e.target);
          if (e.isIntersecting) {
            balkenBeobachter.unobserve(e.target);
            continue;
          }
          const fill = e.target.querySelector('.hw-lade__fill');
          if (fill) fill.style.transform = 'scaleX(0)';
          continue;
        }
        if (!e.isIntersecting) continue;
        balkenBeobachter.unobserve(e.target);
        laden(e.target);
      }
    },
    { rootMargin: '0px 0px 30% 0px', threshold: 0 }
  );

  /* 2. Sichtbarkeit */
  const revealBeobachter = new IntersectionObserver(
    (eintraege) => {
      for (const e of eintraege) {
        if (!e.isIntersecting) continue;
        revealBeobachter.unobserve(e.target);
        e.target.classList.add('is-sichtbar');
      }
    },
    { rootMargin: '0px 0px 35% 0px', threshold: 0 }
  );

  const sammeln = (wurzel = document) => {
    wurzel.querySelectorAll('.hw-lade').forEach((balken) => {
      if (gesehen.has(balken) || balken.closest(AUSNAHME)) return;
      gesehen.add(balken);
      balkenNeu.add(balken);
      balkenBeobachter.observe(balken);
    });

    wurzel.querySelectorAll('[data-hw-reveal]').forEach((el) => {
      if (gesehen.has(el)) return;
      gesehen.add(el);
      revealBeobachter.observe(el);
    });
  };

  // Theme-Editor: Observer der entfernten Section lösen
  const loslassen = (wurzel) => {
    wurzel.querySelectorAll('.hw-lade').forEach((b) => balkenBeobachter.unobserve(b));
    wurzel.querySelectorAll('[data-hw-reveal]').forEach((el) => revealBeobachter.unobserve(el));
  };

  bereit(() => sammeln());
  // Theme-Editor: neu geladene Abschnitte ebenfalls erfassen
  document.addEventListener('shopify:section:load', (e) => sammeln(e.target));
  document.addEventListener('shopify:section:unload', (e) => loslassen(e.target));

  /* 3. Warenkorb: Set-Ladebalken im Drawer und auf der Warenkorbseite */
  const WK_WURZEL = 'cart-drawer-component, .shopify-section';
  const wkLetzte = new WeakMap(); // Wurzel -> zuletzt gezeigter Prozentwert
  const wkBeobachtet = new Map(); // Wurzel -> MutationObserver (Map, damit unload sie findet)
  const wkSkala = (prozent) => `scaleX(${(prozent / 100).toFixed(4)})`;

  // Balken von `von` auf den aktuellen Wert animieren; ohne `von` vom zuletzt gesehenen Wert.
  const wkFuellen = (wurzel, von) => {
    const el = wurzel.querySelector('[data-hw-versand]');
    if (!el) return;
    const neu = Math.min(100, Math.max(0, parseFloat(el.dataset.hwProzent) || 0));
    const alt = von ?? (wkLetzte.has(wurzel) ? wkLetzte.get(wurzel) : neu);
    wkLetzte.set(wurzel, neu);
    if (alt === neu && von === undefined) return;
    const fill = el.querySelector('.hw-versand__fill');
    if (fill && alt !== neu) {
      fill.animate([{ transform: wkSkala(alt) }, { transform: wkSkala(neu) }], { duration: 500, easing: weich });
    }
    const stempel = el.querySelector('.hw-versand__stempel');
    if (stempel) {
      stempel.animate(
        [
          { transform: 'rotate(-6deg) scale(0.6)', opacity: 0 },
          { transform: 'rotate(-6deg) scale(1)', opacity: 1 },
        ],
        { duration: 300, easing: weich }
      );
    }
  };

  // Section-Re-Render (Hydration-Morph ersetzt Attribute und Knoten): einmal pro Frame prüfen.
  const wkBeobachten = (wurzel) => {
    if (wkBeobachtet.has(wurzel)) return;
    wkLetzte.set(wurzel, parseFloat(wurzel.querySelector('[data-hw-versand]')?.dataset.hwProzent) || 0);
    let angefragt = false;
    const mo = new MutationObserver(() => {
      if (angefragt) return;
      angefragt = true;
      requestAnimationFrame(() => {
        angefragt = false;
        wkFuellen(wurzel);
      });
    });
    mo.observe(wurzel, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-hw-prozent'] });
    wkBeobachtet.set(wurzel, mo);
  };

  const warenkorb = (bereich = document) => {
    bereich.querySelectorAll('[data-hw-versand]').forEach((el) => {
      const wurzel = el.closest(WK_WURZEL);
      if (wurzel) wkBeobachten(wurzel);
    });
  };
  bereit(() => {
    warenkorb();
    // Drawer öffnet sich: von 0 auf den aktuellen Wert laden
    document.addEventListener('theme-drawer:open', (e) => {
      const drawer = e.target instanceof Element ? e.target.querySelector('cart-drawer-component') : null;
      if (drawer) wkFuellen(drawer, 0);
    });
    const offen = document.querySelector('theme-drawer#cart-drawer[open] cart-drawer-component');
    if (offen) wkFuellen(offen, 0);
  });
  document.addEventListener('shopify:section:load', (e) => warenkorb(e.target));
  // Theme-Editor: Observer lösen, deren Wurzel in der entfernten Section liegt (auch
  // cart-drawer-component innerhalb des Section-Wrappers) oder nicht mehr im Dokument ist.
  document.addEventListener('shopify:section:unload', (e) => {
    wkBeobachtet.forEach((mo, wurzel) => {
      if (wurzel === e.target || e.target.contains(wurzel) || !wurzel.isConnected) {
        mo.disconnect();
        wkBeobachtet.delete(wurzel);
      }
    });
  });
})();
